/**
 * Which engagement the audit-scoped rail group and workspace tabs are showing.
 *
 * Two rules from the approved UI:
 * 1. The URL wins, so the rail can never name a different client from the page.
 * 2. Nothing is ever auto-picked; with no selection the group is absent.
 *
 * The selected id is client state in `store/workspaceSlice`; the audit itself
 * is server state in `queries/audits`.
 */
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	createContext,
	type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuditQuery } from '../queries/audits';
import { auditPath as auditPathOf } from '../features/modules/flow';
import { useAppDispatch, useAppSelector } from '../store';
import { selectAudit } from '../store/workspaceSlice';
import type { AuditDetail } from '../schemas';

interface WorkspaceState {
	/** The engagement the rail names and links into, or null when none is
	 *  selected (rule 2). */
	current: AuditDetail | null;
	/** This engagement's own open-exception count, straight off `current`. */
	openExceptions: number;
	/** An audit-scoped URL for the current engagement, or the audits list when
	 *  there is none to scope into. */
	auditPath: (suffix: string) => string;
}

const WorkspaceContext = createContext<WorkspaceState | null>(null);

/* An audit and everything under it, under its client and module
   (`/clients/3/modules/o2c/audits/<id>`) or at the old `/audits/<id>`;
   `new` is excluded by name. */
const AUDIT_PATH =
	/^(?:\/clients\/[^/]+\/modules\/[^/]+)?\/audits\/([^/]+)(?:\/|$)/;
const NOT_AN_AUDIT_ID = new Set(['new']);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
	const { pathname } = useLocation();
	const dispatch = useAppDispatch();
	const selected = useAppSelector((state) => state.workspace.selected);

	const routeAuditId = useMemo(() => {
		const found = AUDIT_PATH.exec(pathname)?.[1] ?? null;
		return found && !NOT_AN_AUDIT_ID.has(found) ? found : null;
	}, [pathname]);

	/* Opening an engagement adopts it, so the rail follows the page. The
     reducer persists it, so leaving and coming back keeps the context. */
	useEffect(() => {
		if (!routeAuditId || routeAuditId === selected) return;
		dispatch(selectAudit(routeAuditId));
	}, [dispatch, routeAuditId, selected]);

	/* No fallback beyond the stored choice (rule 2). A deleted audit errors
     here and `current` stays null. */
	const { data } = useAuditQuery(routeAuditId ?? selected);

	const current = data ?? null;
	const openExceptions = current?.openExceptions ?? 0;

	const auditPath = useCallback(
		(suffix: string) =>
			current ? `${auditPathOf(current)}${suffix}` : '/audits',
		[current],
	);

	const value = useMemo<WorkspaceState>(
		() => ({ current, openExceptions, auditPath }),
		[current, openExceptions, auditPath],
	);

	return (
		<WorkspaceContext.Provider value={value}>
			{children}
		</WorkspaceContext.Provider>
	);
}

export function useWorkspace(): WorkspaceState {
	const context = useContext(WorkspaceContext);
	if (!context)
		throw new Error('useWorkspace must be used inside <WorkspaceProvider>');
	return context;
}
